import { convertFromAdaptivaServerTimeToISOString, convertToAdaptivaServerTime, DataVariants, DisplayValue, ETimeUnits, getDaysOfWeekFromByte, IHashMap, lpad, SetOwner, TableRow, TimeService } from '@adaptiva/core/common';
import { IMenuItem, MenuItem } from '@adaptiva/core/menus';
import { IRowEvent, SchemaAndRows } from '@adaptiva/core/tables';
import { AllowedTimeSlot, BaseDeployedEditorComponent, BaseEditorError, DataSetColumn, EBuiltInIds, EditorInput, EEditorErrorType, EJavaClassNames, IBaseEditorError, Schedule, ServiceWindow } from '@adaptiva/data-service';
import { InputComponent } from '@adaptiva/widgets/input';
import { OverlayComponent } from '@adaptiva/widgets/overlay';
import { RadioButtonComponent } from '@adaptiva/widgets/radio-button';
import { ITimeScaleConfiguration, TimeScaleConfiguration, TimeScaleUnit } from '@adaptiva/widgets/time-scale';
import { ToggleSwitchComponent } from '@adaptiva/widgets/toggle-switch';
import { ChangeDetectionStrategy, Component, inject, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { cloneDeep } from 'lodash';
import { BehaviorSubject, map, Observable, tap } from 'rxjs';
import { BaseScheduleTypeComponent } from './components/base-schedule-type.component';
import { EDaysOfWeek, EScheduleType } from './enums';
import { ISchedulePropertyChange, ISchedulePropertyOutput, ITimeSlotTableRow } from './interfaces';

@Component({
	standalone: false,
	selector: 'ada-schedule-editor-area',
	templateUrl: './schedule-editor-area.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScheduleEditorAreaComponent extends BaseDeployedEditorComponent<Schedule> {

	@ViewChild('NameInput') @SetOwner nameInput: InputComponent;

	@ViewChild('UseServerTimeZoneToggle') @SetOwner useServerTimeZoneToggle: ToggleSwitchComponent;

	@ViewChild('StartTimeInput') @SetOwner startTimeInput: InputComponent;

	@ViewChild('EnableEndTimeToggle') @SetOwner enableEndTimeToggle: ToggleSwitchComponent;

	@ViewChild('EndTimeInput') @SetOwner endTimeInput: InputComponent;

	@ViewChild('ScheduleTypeRadioButton') @SetOwner scheduleTypeRadioButton: RadioButtonComponent;

	@ViewChild('LoadLevelingInput') @SetOwner loadLevelingInput: InputComponent;

	@ViewChild('OverrideDurationInput') @SetOwner overrideDurationInput: InputComponent;

	@ViewChild('TimeSlotOverlay') @SetOwner timeSlotOverlay: OverlayComponent;

	@ViewChildren('ScheduleTypeComponent')
	private _scheduleTypeComponents: QueryList<BaseScheduleTypeComponent>;

	public additionalConstraints: boolean = false;

	public readonly dayOptions: DisplayValue<EDaysOfWeek>[] = [
		new DisplayValue<EDaysOfWeek>('Sun', 1 << EDaysOfWeek.SUNDAY),
		new DisplayValue<EDaysOfWeek>('Mon', 1 << EDaysOfWeek.MONDAY),
		new DisplayValue<EDaysOfWeek>('Tue', 1 << EDaysOfWeek.TUESDAY),
		new DisplayValue<EDaysOfWeek>('Wed', 1 << EDaysOfWeek.WEDNESDAY),
		new DisplayValue<EDaysOfWeek>('Thu', 1 << EDaysOfWeek.THURSDAY),
		new DisplayValue<EDaysOfWeek>('Fri', 1 << EDaysOfWeek.FRIDAY),
		new DisplayValue<EDaysOfWeek>('Sat', 1 << EDaysOfWeek.SATURDAY),
	];

	public override readonly className: EJavaClassNames = EJavaClassNames.Schedule;

	public generalSettingsError$: BehaviorSubject<string> = new BehaviorSubject('');

	public saveAsAlertMsg: string;

	public saveAsName: string;

	public saveButtonsEnabled$: BehaviorSubject<boolean> = new BehaviorSubject(false);

	public scheduleTypeError$: BehaviorSubject<string> = new BehaviorSubject('');

	public startTimeError$: BehaviorSubject<string> = new BehaviorSubject('');

	public timeSlotEndTime: string;

	public timeSlotError$: BehaviorSubject<string> = new BehaviorSubject('');

	public timeSlots$: BehaviorSubject<SchemaAndRows> = new BehaviorSubject(new SchemaAndRows([], [], 0));

	public timeSlotStartTime: string;

	public scheduleType: byte;

	public scheduleTypes: typeof EScheduleType = EScheduleType;

	public scheduleTypeOptions: DisplayValue[] = [
		new DisplayValue('ASAP', EScheduleType.ASAP),
		new DisplayValue('Not Recurring', EScheduleType.NOT_RECURRING),
		new DisplayValue('Recurring Interval', EScheduleType.RECURRING_BY_INTERVAL),
		new DisplayValue('Recurring By Day', EScheduleType.RECURRING_BY_DAY),
		new DisplayValue('Recurring By Week', EScheduleType.RECURRING_BY_WEEK),
		new DisplayValue('Recurring Monthly by Date', EScheduleType.RECURRING_BY_DATE),
		new DisplayValue('Recurring Monthly by Last Day', EScheduleType.RECURRING_BY_LAST_DAY),
		new DisplayValue('Recurring Monthly by Day Of The Week', EScheduleType.RECURRING_BY_DAY_OF_WEEK),
	];

	protected readonly offsetTypeOptions: DisplayValue<int>[] = [
		new DisplayValue('Before execution', -1, 'Offsets execution before the scheduled time'),
		new DisplayValue('After execution', 1, 'Offsets execution after the scheduled time'),
	];

	protected readonly offsetTimeScale: ITimeScaleConfiguration = {
		format: TimeScaleConfiguration.SCALE_MILLISECONDS,
		scaleUnits: [
			new TimeScaleUnit('days'),
			new TimeScaleUnit('hours'),
			new TimeScaleUnit('minutes'),
			new TimeScaleUnit('seconds'),
		]
	};

	protected readonly slotTimeScaleConfig: ITimeScaleConfiguration = {
		format: TimeScaleConfiguration.SCALE_MILLISECONDS,
		scaleUnits: [
			new TimeScaleUnit('hours'),
			new TimeScaleUnit('minutes'),
			new TimeScaleUnit('seconds'),
		]
	};

	protected readonly timeSlotsTableMenuItems: MenuItem[] = [
		new MenuItem({
			name: 'Remove Time Slot',
			isDisabled: this.editorIsReadonly,
			onSelect: (_item: IMenuItem<IRowEvent<Schedule>>) => this.removeTimeSlot(_item.data)
		})
	];

	protected timeSlotWeekdaySelections: byte[];

	private readonly _timeService: TimeService = inject(TimeService);

	private _cachedServiceWindow: ServiceWindow;

	private _endTimeEnabled: boolean;

	private _numericEndTime: number;

	private _numericStartTime: number;

	private _timeSlotMap: Map<number, AllowedTimeSlot> = new Map();

	private _timeSlotTableData: SchemaAndRows;

	public get loadLevelingMax(): string {
		return this.getMax(this.state.value.serviceWindow.unitOfLoadLevellingWindowDuration);
	}

	public get overrideDurationMax(): string {
		return this.getMax(this.state.value.serviceWindow.unitOfOverrideDuration);
	}

	public override generateNewObject(_input: EditorInput<Schedule>): Observable<Schedule> {
		return super.generateNewObject(_input).pipe(
			tap((_schedule: Schedule) => {
				_schedule.parentFolderId = _schedule.parentFolderId || EBuiltInIds.UI_SCHEDULE_OBJECTS_ROOT_FOLDER_ID;
			})
		);
	}

	public override beforeSave(): Observable<Schedule> {
		return super.beforeSave().pipe(
			map((_schedule: Schedule) => {
				if (_schedule != null && !Schedule.isRecurring(_schedule.scheduleType))
					_schedule.executionOffset = 0;

				return _schedule;
			})
		);
	}

	public override commitModelToView(): void {
		super.commitModelToView();

		if (this.state.value.scheduleType)
			this.setScheduleType(this.state.value.scheduleType);

		if (this.state.value.startTime && this.startTimeInput) {
			// convert to a date object
			const _startTime: Date = new Date(convertFromAdaptivaServerTimeToISOString(this.state.value.startTime));

			// cache numeric value for comparison
			this._numericStartTime = +_startTime;
			this.startTimeInput.setValue(_startTime);
		}

		// end time
		if (this.state.value.endTime && this.endTimeInput) {
			// end time exists, so set flag
			this._endTimeEnabled = true;
			// convert to date object
			const _endTime: Date = new Date(convertFromAdaptivaServerTimeToISOString(this.state.value.endTime));

			// cache numeric value for comparison
			this._numericEndTime = +_endTime;
			this.endTimeInput.setValue(_endTime);
		}

		if (this.state.value.serviceWindow) {
			this.additionalConstraints = true;
			this.initServiceWindow();
		}

		this.cd.markForCheck();
	}

	public addTimeSlot(_event: boolean): void {
		if (!_event || !this.state.value?.serviceWindow)
			return;

		const [_startTimeHours, _startTimeMinutes]: string[] = this.timeSlotStartTime.split(':');
		const [_endTimeHours, _endTimeMinutes]: string[] = this.timeSlotEndTime.split(':');
		const _daysOfTheWeek: number = this.timeSlotWeekdaySelections.reduce((_total, n) => _total + n, 0);
		const _timeSlot: AllowedTimeSlot = {
			startTimeHours: +_startTimeHours,
			startTimeMinutes: +_startTimeMinutes,
			endTimeHours: +_endTimeHours,
			endTimeMinutes: +_endTimeMinutes,
			daysOfTheWeek: _daysOfTheWeek
		};

		this.state.update('serviceWindow', (_window: ServiceWindow) => {
			if (_window.allowedTimeSlots)
				_window.allowedTimeSlots.push(_timeSlot);
			else
				_window.allowedTimeSlots = [_timeSlot];

			_window.allowedTimeSlotsEnabled = true;

			return _window;
		});

		this.updateTimeSlotTable([this.createTimeSlotTableRow(_timeSlot)]);
		this.timeSlotStartTime = '';
		this.timeSlotEndTime = '';
		this.cd.markForCheck();
	}

	protected clearTimeSlotTable(): void {
		if (!this._timeSlotTableData)
			return;

		this._timeSlotTableData.rows = [];
		this._timeSlotTableData.totalRowCount = 0;
	}

	protected createTimeSlotTableRow(_timeSlot: AllowedTimeSlot): ITimeSlotTableRow {
		const _startTime: string = `${lpad(_timeSlot.startTimeHours, 2)}:${lpad(_timeSlot.startTimeMinutes, 2)}`;
		const _endTime: string = `${lpad(_timeSlot.endTimeHours, 2)}:${lpad(_timeSlot.endTimeMinutes, 2)}`;
		const _days: string = getDaysOfWeekFromByte(_timeSlot.daysOfTheWeek).join(', ');
		const _id: number = Math.floor(Math.random() * 1000000);

		this._timeSlotMap.set(_id, _timeSlot);

		return { startTime: _startTime, endTime: _endTime, days: _days, id: _id };
	}

	public enableEndTimeChanged(_event: boolean): void {
		this._endTimeEnabled = _event;
		this.endTimeInput.isDisabled = !_event;
		this.setEndTime(null);
	}

	public endTimeValid(): boolean {
		// no end time no problem
		if (!this._endTimeEnabled || (!this.state.value.endTime && !this.state.value.endTimeInUTC))
			return true;

		// end time enabled and entered
		if (this.state.value.endTime) {
			// end time but no start time
			if (!this.state.value.startTime)
				return false;

			// start time is after end time
			if (this._numericStartTime > this._numericEndTime)
				return false;
		}

		return true;
	}

	protected getMax(_unit: Schedule.EIntervalUnits): string {
		switch (_unit) {
			case Schedule.EIntervalUnits.MINUTES: return '60';
			case Schedule.EIntervalUnits.DAYS: return '24';
			case Schedule.EIntervalUnits.HOURS: return '100';
			default: return '127';
		}
	}

	public initServiceWindow(): void {
		setTimeout(() => {
			const _sw: ServiceWindow = this.state.value.serviceWindow;

			// Time Slot Table
			if (_sw.allowedTimeSlots) {
				this.clearTimeSlotTable();
				this.updateTimeSlotTable(
					_sw.allowedTimeSlots.map(slot => this.createTimeSlotTableRow(slot))
				);
			}

			// Convert milliseconds for load level and override inputs
			if (_sw.unitOfLoadLevellingWindowDuration && _sw.loadLevelingDurationInMilliseconds) {
				const _duration: long = this._timeService.millisecondsToTimeUnit(
					_sw.unitOfLoadLevellingWindowDuration,
					_sw.loadLevelingDurationInMilliseconds
				);

				if (_duration)
					this.loadLevelingInput.setValue(_duration.toString());
			}

			if (_sw.unitOfOverrideDuration && _sw.overrideDurationInMilliseconds) {
				const _duration: long = this._timeService.millisecondsToTimeUnit(_sw.unitOfOverrideDuration, _sw.overrideDurationInMilliseconds);
				// if (_duration)
				// 	this.overrideDurationInput.setValue(_duration.toString());
			}

			this.cd.markForCheck();
		}, 300);
	}

	public openTimeSlotOverlay(_event: MouseEvent): void {
		this.timeSlotWeekdaySelections = [];
		this.timeSlotOverlay.open();
	}

	public removeTimeSlot(_event: object): void {
		if (!_event['row'])
			return;

		if (!_event['row']['id'])
			return;

		this._timeSlotTableData = this._timeSlotTableData.clone();
		this._timeSlotTableData.rows = this._timeSlotTableData.rows.filter(d => d['id'] !== _event['row']['id']);
		this.timeSlots$.next(this._timeSlotTableData);
		this.state.update('serviceWindow', (_window: ServiceWindow) => {
			_window.allowedTimeSlots = this.state.value.serviceWindow.allowedTimeSlots.filter(
				s => s !== this._timeSlotMap.get(_event['row']['id'])
			);
			_window.allowedTimeSlotsEnabled = _window.allowedTimeSlots.length > 0;

			return _window;
		});
	}

	public setDescription(_event: string): void {
		this.state.set('description', _event?.trim());
	}

	public setEndTime(_event: string): void {
		if (!_event) {
			this.state.set('endTime', null);
			this.state.set('endTimeInUTC', 0);
		} else {
			const _date: string = new Date(+_event).toString();

			this.state.set('endTime', convertToAdaptivaServerTime(_date));
			this._numericEndTime = +_event;
		}
	}

	public setLoadLevelingDuration(_duration: long): void {
		if (this.state.value.serviceWindow.loadLevelingDurationInMilliseconds === _duration)
			return;

		this.state.update('serviceWindow', (_window: ServiceWindow) => {
			_window.loadLevelingDurationInMilliseconds = _duration;

			return _window;
		});
	}

	public setName(_event: string): void {
		this.state.set('name', _event?.trim());
	}

	public setOverrideDuration(_duration: long): void {
		if (this.state.value.serviceWindow.overrideDurationInMilliseconds === _duration)
			return;

		this.state.update('serviceWindow', (_window: ServiceWindow) => {
			_window.overrideDurationInMilliseconds = _duration;

			return _window;
		});
	}

	public setScheduleType(_scheduleType: byte): void {
		if (!this.state.value)
			return;

		this.state.set('scheduleType', +_scheduleType);

		// disable start time input if it's asap
		if (_scheduleType === 1) {
			this.startTimeInput.isDisabled = true;
			this.useServerTimeZoneToggle.isDisabled = true;
			this.enableEndTimeToggle.isDisabled = true;
		} else {
			this.startTimeInput.isDisabled = false;
			this.useServerTimeZoneToggle.isDisabled = false;
			this.enableEndTimeToggle.isDisabled = false;
		}
	}

	public setServiceWindowUseServerTimeZone(_event: boolean): void {
		this.state.update('serviceWindow', (_window: ServiceWindow) => {
			_window.useServerTimeZone = _event;

			return _window;
		});
	}

	// start/end inputs are set to output as integers
	public setStartTime(_event: string): void {
		if (!_event) {
			this.state.set('startTime', null);
			this.state.set('startTimeInUTC', 0);
		} else {
			// convert number to date string
			const _date: string = new Date(+_event).toString();

			this.state.set('startTime', convertToAdaptivaServerTime(_date));
			this._numericStartTime = +_event;
		}
	}

	public setUnitOfLevelingWindowDuration(_event: any): void {
		if (this.state.value.serviceWindow.unitOfLoadLevellingWindowDuration === +_event)
			return;

		this.state.value.serviceWindow.unitOfLoadLevellingWindowDuration = +_event;
		this.setLoadLevelingDuration(this.loadLevelingInput.value);
	}

	public setUnitOfOverrideDuration(_event: any): void {
		if (this.state.value.serviceWindow.unitOfOverrideDuration === +_event)
			return;

		this.state.value.serviceWindow.unitOfOverrideDuration = +_event;
		this.setOverrideDuration(this.overrideDurationInput.value);
	}

	public setUseAdditionalTimeConstraints(_event: boolean): void {
		if (_event) {
			this.state.set('serviceWindow', this._cachedServiceWindow || new ServiceWindow());
			this.initServiceWindow();
		} else {
			this._cachedServiceWindow = cloneDeep(this.state.value.serviceWindow);
			this.state.set('serviceWindow', null);
		}

		this.cd.markForCheck();
	}

	public setUseServerTimeZone(_event: boolean): void {
		this.state.set('useServerTimeZone', _event);
	}

	protected updateTimeSlotTable(_newData: object[]): void {
		// simple map for deduping
		const _rowMap: IHashMap<boolean> = {};
		// get existing rows if any
		const _currentRows: TableRow[] = this._timeSlotTableData ? this._timeSlotTableData.rows : [];
		// merge rows together
		const _mergedRows: TableRow[] = [..._currentRows, ..._newData].filter(row => {
			if (!_rowMap[row['id']]) {
				_rowMap[row['id']] = true;

				return true;
			}

			return false;
		});

		this._timeSlotTableData = new SchemaAndRows(
			[
				new DataSetColumn({ columnName: 'startTime', columnDisplay: 'Start Time', type: DataVariants.TEXT }),
				new DataSetColumn({ columnName: 'endTime', columnDisplay: 'End Time', type: DataVariants.TEXT }),
				new DataSetColumn({ columnName: 'days', columnDisplay: 'Days of the Week', type: DataVariants.TEXT })
			],
			_mergedRows,
			_mergedRows.length
		);
		this.timeSlots$.next(this._timeSlotTableData);
	}

	public updateTimeSlotWeekdaySelections(_event: any[]): void {
		const _selections: number[] = _event.map(day => +day);

		this.timeSlotWeekdaySelections = _selections;
	}

	protected override validate(): BaseEditorError[] {
		let errors: BaseEditorError[] = [];

		if (!this.state.value.name) {
			errors.push(new BaseEditorError(
				EEditorErrorType.Error,
				this.nameInput.guid,
				['Name', 'Schedule name required']
			));
		}

		if (!this.endTimeValid()) {
			errors.push(new BaseEditorError(
				EEditorErrorType.Error,
				this.endTimeInput.guid,
				['End Time', 'End time must be greater than start time']
			));
		}

		if (!this.state.value.scheduleType) {
			errors.push(new BaseEditorError(
				EEditorErrorType.Error,
				this.scheduleTypeRadioButton.guid,
				['Schedule Type', 'Must select a schedule type']
			));
		}

		this._scheduleTypeComponents.toArray().forEach(_component => {
			const _componentErrors: IBaseEditorError[] = _component.validate() || [];

			errors = [...errors, ..._componentErrors];
		});

		return errors;
	}

	public validateTimeSlot(): boolean {
		if (!this.timeSlotStartTime) {
			this.timeSlotError$.next('Must specify a start time');

			return false;
		}

		if (!this.timeSlotEndTime || this.timeSlotEndTime <= this.timeSlotStartTime) {
			this.timeSlotError$.next('End time must be greater than start time');

			return false;
		}

		if (!this.timeSlotWeekdaySelections || !this.timeSlotWeekdaySelections.length) {
			this.timeSlotError$.next('Must select at least one day');

			return false;
		}

		this.timeSlotError$.next('');

		return true;
	}

	public updateFromScheduleType(_output: ISchedulePropertyOutput): void {
		_output.changes.forEach((_change: ISchedulePropertyChange) => this.state.set(_change.propertyName as keyof Schedule, _change.value));
	}

	public intervalUnitsToTimeUnits(_interval: Schedule.EIntervalUnits): ETimeUnits {
		// the values in Schedule.java don't match ETimeUnits
		switch (_interval) {
			case Schedule.EIntervalUnits.DAYS: return ETimeUnits.DAYS;
			case Schedule.EIntervalUnits.HOURS: return ETimeUnits.HOURS;
			case Schedule.EIntervalUnits.MINUTES: return ETimeUnits.MINUTES;
			default: return ETimeUnits.HOURS; // default in setting the combo component
		}
	}

	protected setScheduleOffset(_offset: int): void {
		this.state.update('executionOffset', (_prev: int) => {
			return Math.abs(_offset) * (Math.sign(_prev) || 1);
		});
	}

	protected toggleOffsetType(_sign: int): void {
		this.state.update('executionOffset', (_offset: int) => {
			return Math.abs(_offset) * _sign;
		});
	}

}
